/**
 * /manufacturing dashboard page.
 *
 * Five-tab read-only view of the Odoo MRP + Onshape CAD mirror. The BOM tab
 * hosts an Explore/Simulate mode toggle (?mode=explore|simulate) — the
 * two surfaces share a layout primitive (BomTreeCore) but differ in
 * per-node chrome.
 *
 *   Overview    -- ManufacturingFlow (default)
 *   Production  -- ProductionKanban
 *   BOM         -- BomTab (Explore | Simulate)
 *   Tables      -- TruthTellerTables
 *   Drift       -- DriftView (Phase 4)
 *
 * Tab + mode state is held in the URL via useSearchParams so refreshes
 * and deep-links survive. SyncStatusBanner stays above the tabs as a
 * persistent header so the operator always sees the snapshot freshness.
 *
 * Backwards-compat: `?tab=simulate` (the old shape) rewrites to
 * `?tab=bom&mode=simulate` on mount so previously shared links don't
 * 404 into an unknown tab.
 *
 * Auth gate: MANUFACTURING_ALLOWED_EMAILS env on the backend ->
 * /api/manufacturing/access. Same pattern as /agents and
 * /website-analytics. Defense-in-depth (push allowlist into RLS) is
 * tracked in #722.
 *
 * Page testids satisfy the e2e contract in
 * tests/e2e-browser/tests/manufacturing.spec.ts.
 */
import { useCallback, useEffect, useMemo } from 'react';
import { Alert, Box, Button, Container, Skeleton, Tab, Tabs, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useManufacturingAccess } from '../hooks/useManufacturingAccess';
import { useTranslation } from '../i18n';
import {
  manufacturingKeys,
  useManufacturingSyncStatusQuery,
  useManufacturingProductsQuery,
  useManufacturingWorkCentersQuery,
  useManufacturingBomsQuery,
  useManufacturingProductionOrdersQuery,
  useManufacturingOnshapeDocumentsQuery,
  useManufacturingOnshapePartsQuery,
  useManufacturingDriftQuery,
  useManufacturingOnshapePartsByCodeQuery,
} from '../components/Manufacturing/Manufacturing.queries';
import { SyncStatusBanner } from '../components/Manufacturing/SyncStatusBanner';
import { ManufacturingFlow } from '../components/Manufacturing/ManufacturingFlow';
import { ProductionKanban } from '../components/Manufacturing/ProductionKanban';
import { BomTab, type BomMode } from '../components/Manufacturing/BomTab';
import { TruthTellerTables } from '../components/Manufacturing/TruthTellerTables';
import { DriftView } from '../components/Manufacturing/DriftView';

type TabKey = 'overview' | 'production' | 'bom' | 'tables' | 'drift';

const TAB_DEFS: Array<{ key: TabKey; label: string }> = [
  { key: 'overview', label: 'Overview' },
  { key: 'production', label: 'Production' },
  { key: 'bom', label: 'BOM' },
  { key: 'tables', label: 'Tables' },
  { key: 'drift', label: 'Drift' },
];

const VALID_TABS = new Set<TabKey>(TAB_DEFS.map((t) => t.key));
const VALID_BOM_MODES = new Set<BomMode>(['explore', 'simulate']);

export function ManufacturingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { hasAccess, loading: accessLoading } = useManufacturingAccess();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL is the source of truth for the active tab. Backwards-compat: the
  // original 6-tab layout had a top-level `?tab=simulate`. Map it to the
  // merged shape (BOM tab + Simulate mode) synchronously so the first
  // render shows the right surface without a flash of Overview.
  const rawTab = searchParams.get('tab');
  const rawMode = searchParams.get('mode');
  const legacySimulate = rawTab === 'simulate';
  const tabParam = (legacySimulate ? 'bom' : rawTab) as TabKey | null;
  const activeTab: TabKey = tabParam && VALID_TABS.has(tabParam) ? tabParam : 'overview';

  const modeParam = (legacySimulate ? 'simulate' : rawMode) as BomMode | null;
  const bomMode: BomMode = modeParam && VALID_BOM_MODES.has(modeParam) ? modeParam : 'explore';

  // Rewrite the URL too so previously shared `?tab=simulate` links don't
  // stay legacy-shaped in the address bar. `replace: true` so we don't
  // pollute the back-button history with a redirect step.
  useEffect(() => {
    if (legacySimulate) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', 'bom');
      next.set('mode', 'simulate');
      setSearchParams(next, { replace: true });
    }
  }, [legacySimulate, searchParams, setSearchParams]);

  // STD-015: each of the eight reads is its own query, enabled only once the
  // access check passes. The page still presents a single combined loading
  // state and a single combined error (below) so the UX matches the old
  // Promise.all behavior. The shared query key prefix (manufacturingKeys.all)
  // lets the Refresh button invalidate the whole domain in one call.
  const syncStatusQuery = useManufacturingSyncStatusQuery(hasAccess);
  const productsQuery = useManufacturingProductsQuery(hasAccess);
  const workCentersQuery = useManufacturingWorkCentersQuery(hasAccess);
  const bomsQuery = useManufacturingBomsQuery(hasAccess);
  const productionOrdersQuery = useManufacturingProductionOrdersQuery(hasAccess);
  const onshapeDocsQuery = useManufacturingOnshapeDocumentsQuery(hasAccess);
  const onshapePartsQuery = useManufacturingOnshapePartsQuery(hasAccess);
  const driftQuery = useManufacturingDriftQuery(hasAccess);

  // Memoise the array-shaped reads so a stable identity flows to the tab
  // components (the old state-based page had the same guarantee — child render
  // memos key off these). Scalars (syncStatus, drift) are fine inline.
  const syncStatus = syncStatusQuery.data ?? null;
  const products = useMemo(() => productsQuery.data ?? [], [productsQuery.data]);
  const workCenters = useMemo(() => workCentersQuery.data ?? [], [workCentersQuery.data]);
  const boms = useMemo(() => bomsQuery.data ?? [], [bomsQuery.data]);
  const productionOrders = useMemo(
    () => productionOrdersQuery.data ?? [],
    [productionOrdersQuery.data]
  );
  const onshapeDocs = useMemo(() => onshapeDocsQuery.data ?? [], [onshapeDocsQuery.data]);
  const onshapeParts = useMemo(() => onshapePartsQuery.data ?? [], [onshapePartsQuery.data]);
  const drift = driftQuery.data ?? null;

  const coreQueries = [
    syncStatusQuery,
    productsQuery,
    workCentersQuery,
    bomsQuery,
    productionOrdersQuery,
    onshapeDocsQuery,
    onshapePartsQuery,
    driftQuery,
  ];
  // Initial load: true only until each query first resolves (drives the view
  // skeletons). A manual refresh keeps existing data on screen instead of
  // flashing skeletons.
  const loading = coreQueries.some((q) => q.isLoading);
  // Any fetch in flight (initial or refresh) — drives the Refresh button's
  // disabled state and label so it can't be triggered twice concurrently.
  const refreshing = coreQueries.some((q) => q.isFetching);
  // Combined error: surfaced once via the page Alert. The global QueryCache
  // handler also toasts each failure; the Alert gives persistent context.
  const hasError = coreQueries.some((q) => q.isError);

  // Most-recent successful fetch across the eight queries, used for the
  // "Last refresh" caption (replaces the old lastRefreshedAt state). Listed
  // explicitly (not via coreQueries) so exhaustive-deps stays satisfied
  // without a disable directive.
  const lastRefreshedAt = useMemo(() => {
    const times = [
      syncStatusQuery.dataUpdatedAt,
      productsQuery.dataUpdatedAt,
      workCentersQuery.dataUpdatedAt,
      bomsQuery.dataUpdatedAt,
      productionOrdersQuery.dataUpdatedAt,
      onshapeDocsQuery.dataUpdatedAt,
      onshapePartsQuery.dataUpdatedAt,
      driftQuery.dataUpdatedAt,
    ].filter((t): t is number => typeof t === 'number' && t > 0);
    if (times.length === 0) return null;
    return new Date(Math.max(...times)).toISOString();
  }, [
    syncStatusQuery.dataUpdatedAt,
    productsQuery.dataUpdatedAt,
    workCentersQuery.dataUpdatedAt,
    bomsQuery.dataUpdatedAt,
    productionOrdersQuery.dataUpdatedAt,
    onshapeDocsQuery.dataUpdatedAt,
    onshapePartsQuery.dataUpdatedAt,
    driftQuery.dataUpdatedAt,
  ]);

  // Manual refresh: invalidate the whole manufacturing domain so every query
  // (including parts-by-code) refetches. Mirrors the old loadAll().
  const loadAll = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: manufacturingKeys.all });
  }, [queryClient]);

  // Once BOMs + products land, batch-resolve every default_code referenced in
  // any BOM (as the root product OR as a bom_line) into its Onshape part +
  // drawing/PDF. Single round-trip, cached until the next refresh. Replaces
  // the old substring-on-document-name heuristic in BomTreeExplore. Capped at
  // 500 (server-side limit), sliced deterministically so the earliest codes
  // (root + first lines) always win when truncated.
  const partsByCodeCodes = useMemo(() => {
    if (boms.length === 0 || products.length === 0) return [];
    const productById = new Map(products.map((p) => [p.id, p]));
    const codes = new Set<string>();
    for (const b of boms) {
      const head = productById.get(b.product_id);
      if (head?.default_code) codes.add(head.default_code);
      for (const line of b.bom_lines) {
        const child = productById.get(line.product_id);
        if (child?.default_code) codes.add(child.default_code);
      }
    }
    return Array.from(codes).slice(0, 500);
  }, [boms, products]);

  const partsByCodeQuery = useManufacturingOnshapePartsByCodeQuery(
    partsByCodeCodes,
    hasAccess && partsByCodeCodes.length > 0
  );
  const partsByCode = useMemo(() => partsByCodeQuery.data ?? {}, [partsByCodeQuery.data]);

  const handleTabChange = useCallback(
    (_e: React.SyntheticEvent, value: TabKey) => {
      // Replace the tab param without dropping anything else the URL
      // might be carrying. Drop `mode` when leaving the BOM tab so the
      // URL doesn't accumulate stale state.
      const next = new URLSearchParams(searchParams);
      next.set('tab', value);
      if (value !== 'bom') next.delete('mode');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleBomModeChange = useCallback(
    (mode: BomMode) => {
      const next = new URLSearchParams(searchParams);
      next.set('tab', 'bom');
      next.set('mode', mode);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Memoised so the tab content components don't see a fresh array
  // identity on unrelated rerenders.
  const tabContent = useMemo(() => {
    switch (activeTab) {
      case 'overview':
        return (
          <ManufacturingFlow products={products} boms={boms} productionOrders={productionOrders} />
        );
      case 'production':
        return <ProductionKanban orders={productionOrders} products={products} loading={loading} />;
      case 'bom':
        return (
          <BomTab
            mode={bomMode}
            onModeChange={handleBomModeChange}
            boms={boms}
            products={products}
            onshapeDocuments={onshapeDocs}
            partsByCode={partsByCode}
            // Reuse the sync-status banner data so the "stock as of …"
            // caption works in Simulate mode even before the user clicks
            // Simulate (the report's own stock_synced_at field overrides
            // this once a result is back).
            stockSyncedAtFallback={
              syncStatus?.entries.find(
                (e) => e.schema_name === 'manufacturing' && e.table_name === 'stock_quants'
              )?.finished_at ?? null
            }
            loading={loading}
          />
        );
      case 'tables':
        return (
          <TruthTellerTables
            products={products}
            workCenters={workCenters}
            onshapeDocuments={onshapeDocs}
            onshapeParts={onshapeParts}
            loading={loading}
          />
        );
      case 'drift':
        return <DriftView drift={drift} loading={loading} />;
    }
  }, [
    activeTab,
    bomMode,
    handleBomModeChange,
    products,
    boms,
    productionOrders,
    onshapeDocs,
    onshapeParts,
    partsByCode,
    workCenters,
    drift,
    loading,
    syncStatus,
  ]);

  if (accessLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
      </Container>
    );
  }

  if (!hasAccess) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }} data-testid="manufacturing-access-denied">
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')} sx={{ mb: 2 }}>
          Back to Dashboard
        </Button>
        <Alert severity="warning">
          You do not have access to the /manufacturing page. If you believe this is wrong, ask an
          admin to add your email to <code>MANUFACTURING_ALLOWED_EMAILS</code>.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }} data-testid="manufacturing-page">
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')}>
          Back
        </Button>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" component="h1">
            Manufacturing
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {lastRefreshedAt
              ? `Last refresh: ${new Date(lastRefreshedAt).toLocaleString()}`
              : 'Not yet loaded'}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadAll}
          disabled={refreshing}
        >
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </Button>
      </Box>

      {hasError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('manufacturing.failedToLoad')}
        </Alert>
      )}

      {/* Persistent header above the tabs -- snapshot freshness is always
          relevant regardless of which tab the operator is on. */}
      <SyncStatusBanner status={syncStatus} loading={loading} />

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        {TAB_DEFS.map((t) => (
          <Tab
            key={t.key}
            value={t.key}
            label={t.label}
            data-testid={`manufacturing-tab-${t.key}`}
          />
        ))}
      </Tabs>

      {tabContent}
    </Container>
  );
}
